import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Tab {
  value: string;
  label: string;
}

interface TabBarProps {
  tabs: Tab[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
  mobileMode?: "select" | "scroll";
}

function TabButton({
  tab,
  active,
  onChange,
}: {
  tab: Tab;
  active: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onChange(tab.value)}
      className={cn(
        "h-9 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition-all duration-200",
        active
          ? "bg-primary text-primary-foreground shadow-sm shadow-primary/25"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {tab.label}
    </button>
  );
}

export function TabBar({
  tabs,
  value,
  onChange,
  ariaLabel,
  className,
  mobileMode = "select",
}: TabBarProps) {
  const selected = tabs.find((tab) => tab.value === value);
  const listClassName =
    "items-center gap-1 rounded-xl border border-border/70 bg-card/60 p-1 shadow-sm";

  return (
    <div className={cn("w-full sm:w-auto", className)}>
      {mobileMode === "select" ? (
        <div className="sm:hidden">
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger aria-label={ariaLabel} className="h-10 w-full rounded-xl">
              <SelectValue>{selected?.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {tabs.map((tab) => (
                <SelectItem key={tab.value} value={tab.value}>
                  {tab.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="-mx-1 overflow-x-auto px-1 pb-1 sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div role="tablist" aria-label={ariaLabel} className={cn("flex w-max", listClassName)}>
            {tabs.map((tab) => (
              <TabButton key={tab.value} tab={tab} active={tab.value === value} onChange={onChange} />
            ))}
          </div>
        </div>
      )}

      <div role="tablist" aria-label={ariaLabel} className={cn("hidden sm:flex", listClassName)}>
        {tabs.map((tab) => (
          <TabButton key={tab.value} tab={tab} active={tab.value === value} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}
